import apiClient from './api';
import type { AccessPolicy, PaginatedResponse, PaginationParams } from '@/types';

export const policyService = {
  /**
   * Get all access policies
   */
  async getPolicies(params?: PaginationParams): Promise<PaginatedResponse<AccessPolicy>> {
    const { data } = await apiClient.get<PaginatedResponse<AccessPolicy>>('/access-policies', { params });
    return data;
  },

  /**
   * Get single policy by ID
   */
  async getPolicy(id: string): Promise<AccessPolicy> {
    const { data } = await apiClient.get<AccessPolicy>(`/access-policies/${id}`);
    return data;
  },

  /**
   * Create new policy
   */
  async createPolicy(policyData: Partial<AccessPolicy>): Promise<AccessPolicy> {
    const { data } = await apiClient.post<AccessPolicy>('/access-policies', policyData);
    return data;
  },

  /**
   * Update policy
   */
  async updatePolicy(id: string, policyData: Partial<AccessPolicy>): Promise<AccessPolicy> {
    const { data } = await apiClient.put<AccessPolicy>(`/access-policies/${id}`, policyData);
    return data;
  },

  /**
   * Delete policy
   */
  async deletePolicy(id: string): Promise<void> {
    await apiClient.delete(`/access-policies/${id}`);
  },

  /**
   * Test policy
   */
  async testPolicy(policyData: any): Promise<{ allowed: boolean; reason: string }> {
    const { data } = await apiClient.post('/access-policies/test', policyData);
    return data;
  },

  /**
   * Get policy evaluation logs
   */
  async getPolicyLogs(params?: PaginationParams) {
    const { data } = await apiClient.get('/access-policies/logs/list', { params });
    return data;
  },

  /**
   * Get IP lists
   */
  async getIpLists(params?: PaginationParams) {
    const { data } = await apiClient.get('/access-policies/ip-lists', { params });
    return data;
  },

  /**
   * Create IP list
   */
  async createIpList(ipListData: any) {
    const { data } = await apiClient.post('/access-policies/ip-lists', ipListData);
    return data;
  },

  /**
   * Update IP list
   */
  async updateIpList(id: string, ipListData: any) {
    const { data } = await apiClient.put(`/access-policies/ip-lists/${id}`, ipListData);
    return data;
  },

  /**
   * Delete IP list
   */
  async deleteIpList(id: string): Promise<void> {
    await apiClient.delete(`/access-policies/ip-lists/${id}`);
  },

  /**
   * Get geo rules
   */
  async getGeoRules(params?: PaginationParams) {
    const { data } = await apiClient.get('/access-policies/geo-rules', { params });
    return data;
  },

  /**
   * Create geo rule
   */
  async createGeoRule(geoRuleData: any) {
    const { data } = await apiClient.post('/access-policies/geo-rules', geoRuleData);
    return data;
  },
};
